// config/impuestos.js
/**
 * Parámetros fiscales venezolanos, en un solo lugar.
 *
 * Estaban dispersos como números sueltos en tres controladores (`* 0.16`), así
 * que agregar la alícuota reducida obligaba a tocar cada uno. Acá viven juntos y
 * un contador puede revisarlos sin leer el resto del código.
 */

/**
 * Alícuotas del IVA.
 *
 * La adicional no es un impuesto aparte: son 15 puntos que se suman a la general
 * para bienes suntuarios, de ahí el 31% total.
 */
const ALICUOTAS_IVA = {
    general: { valor: 0.16, etiqueta: 'General (16%)', porcentaje: 16 },
    reducida: { valor: 0.08, etiqueta: 'Reducida (8%)', porcentaje: 8 },
    adicional: { valor: 0.31, etiqueta: 'Suntuaria (31%)', porcentaje: 31 }
};

const ALICUOTA_POR_DEFECTO = 'general';

/**
 * Impuesto a las Grandes Transacciones Financieras.
 *
 * Grava los pagos en moneda extranjera o criptoactivos, sin monto mínimo. Sólo
 * lo perciben los contribuyentes especiales, así que se calcula únicamente si la
 * empresa está marcada como tal y el documento no está en bolívares.
 *
 * Sobre la base hay ambigüedad: la ley no dejó claro si incluye el IVA. Acá se
 * calcula sobre el total con IVA, que es la interpretación más extendida; si el
 * contador de una empresa sostiene lo contrario, se cambia BASE_INCLUYE_IVA.
 */
const IGTF = {
    alicuota: 0.03,
    porcentaje: 3,
    BASE_INCLUYE_IVA: true
};

/** Devuelve la tasa a aplicar a un ítem según su tipo fiscal y su alícuota. */
const tasaDeItem = (taxType, alicuota) => {
    if (taxType !== 'gravado') return 0;
    const elegida = ALICUOTAS_IVA[alicuota] || ALICUOTAS_IVA[ALICUOTA_POR_DEFECTO];
    return elegida.valor;
};

/**
 * IGTF de un documento.
 * @param {object} datos - { moneda, subtotal, iva, esContribuyenteEspecial }
 */
const calcularIgtf = ({ moneda, subtotal = 0, iva = 0, esContribuyenteEspecial }) => {
    if (!esContribuyenteEspecial) return 0;
    if (!moneda || moneda === 'VES') return 0; // sólo pagos en divisa
    const base = IGTF.BASE_INCLUYE_IVA ? subtotal + iva : subtotal;
    return Number((base * IGTF.alicuota).toFixed(2));
};

const dosDecimales = (n) => Number((Number(n) || 0).toFixed(2));

/**
 * Totales de un documento a partir de sus ítems.
 *
 * Es el único lugar donde se decide cuánto impuesto lleva un documento. Estaba
 * repetido en el pre-save de la factura y en el del documento, y esa copia se
 * desincronizó dos veces: cuando se agregaron las alícuotas, la factura se
 * actualizó y el presupuesto siguió cobrando un 16% parejo, así que una
 * cotización al 8% se aprobaba por un total y se facturaba por otro.
 *
 * No toca la base de datos ni depende de mongoose: recibe ítems y devuelve
 * números. Por eso se puede probar sola, que es la otra mitad del problema.
 *
 * Normaliza cada ítem sobre la marcha —le fija `taxType`, `alicuota` y
 * `taxExempt`— porque el documento guardado tiene que decir explícitamente qué
 * tratamiento recibió cada renglón: es lo que después lee el Libro de Ventas.
 *
 * @param {Array} items
 * @param {number} igtf - Ya calculado por quien sabe si la empresa lo percibe.
 */
const calcularTotalesDeItems = (items = [], igtf = 0) => {
    const bases = { general: 0, reducida: 0, adicional: 0 };
    let gravado = 0;
    let exento = 0;
    let noGravado = 0;

    (Array.isArray(items) ? items : []).forEach((item) => {
        const cantidad = Number(item.quantity) || 0;
        const precio = Number(item.price) || 0;
        // Se prefiere el subtotal que traiga el ítem: puede venir con un
        // descuento de renglón ya aplicado.
        const monto = Number(item.subtotal) || cantidad * precio;

        // El tipo fiscal manda; `taxExempt` quedó por compatibilidad.
        const taxType = item.taxType || (item.taxExempt ? 'exento' : 'gravado');
        item.taxType = taxType;
        item.taxExempt = taxType === 'exento';

        if (taxType === 'exento') {
            exento += monto;
            return;
        }
        if (taxType === 'no_gravado') {
            noGravado += monto;
            return;
        }

        gravado += monto;
        const alicuota = ALICUOTAS_IVA[item.alicuota] ? item.alicuota : ALICUOTA_POR_DEFECTO;
        item.alicuota = alicuota;
        bases[alicuota] += monto;
    });

    const ivaGeneral = dosDecimales(bases.general * ALICUOTAS_IVA.general.valor);
    const ivaReducida = dosDecimales(bases.reducida * ALICUOTAS_IVA.reducida.valor);
    const ivaAdicional = dosDecimales(bases.adicional * ALICUOTAS_IVA.adicional.valor);
    const impuesto = dosDecimales(ivaGeneral + ivaReducida + ivaAdicional);
    const subtotal = dosDecimales(gravado + exento + noGravado);

    return {
        subtotalGravado: dosDecimales(gravado),
        subtotalExento: dosDecimales(exento),
        subtotalNoGravado: dosDecimales(noGravado),
        baseGeneral: dosDecimales(bases.general),
        baseReducida: dosDecimales(bases.reducida),
        baseAdicional: dosDecimales(bases.adicional),
        ivaGeneral,
        ivaReducida,
        ivaAdicional,
        impuesto,
        subtotal,
        total: dosDecimales(subtotal + impuesto + (Number(igtf) || 0))
    };
};

/** El IGTF como entero, para mostrarlo en pantalla ("IGTF 3%"). */
const IGTF_PORCENTAJE = Math.round(IGTF.alicuota * 100);

module.exports = {
    ALICUOTAS_IVA,
    IGTF_PORCENTAJE,
    ALICUOTA_POR_DEFECTO,
    IGTF,
    tasaDeItem,
    calcularIgtf,
    calcularTotalesDeItems
};
